// External libraries
import React from "react";
import Select from "react-select";

// Project components
import Button from "../../../../Components/Button";
import Multiselect from "../../../../Components/Inputs/Multiselect";
import TextInput from "../../../../Components/Inputs/Text";
import Spacer from "../../../../Components/Spacer";
import { Text } from "../../../../Components/Text";

// Feature/context-specific hooks and constants
import { useConfiguration } from "../../contexts/ConfigurationContext";
import { useCustomizedData } from "../../contexts/CustomizedDataContext";

// Local/relative feature components
import { EmployeesTable, CustomFieldsTable, EmployeeGroupTable } from "./tables";

const CUSTOM_FIELD_TYPES = [
	{
		label: "Text",
		value: "text",
	},
	{
		label: "Date",
		value: "date",
	},
	{
		label: "Person",
		value: "person",
	},
	{
		label: "People",
		value: "people",
	},
	{
		label: "Email",
		value: "email",
	},
	{
		label: "Monetary",
		value: "monetary",
	},
	{
		label: "Single Select",
		value: "single-select",
	},
	{
		label: "Multi Select",
		value: "multi-select",
	},
	{
		label: "Large Text Box",
		value: "large-text-box",
	},
	{
		label: "Phone",
		value: "phone",
	},
	{
		label: "Textbox",
		value: "textbox",
	},
	{
		label: "Textarea",
		value: "textarea",
	},
];

const BOOLEAN_OPTIONS = [
	{ label: "True", value: "true" },
	{ label: "False", value: "false" },
];

const EmployeeData = () => {
	const { showError, hideError, error } = useConfiguration();

	const {
		employeeData,
		updateEmployeeData,
		addEmployee: addEmployeeToContext,
		editEmployee: editEmployeeInContext,
		deleteEmployee: deleteEmployeeFromContext,
		cancelEditEmployee,
		customFieldData,
		updateCustomFieldData,
		addCustomField: addCustomFieldToContext,
		editCustomField: editCustomFieldInContext,
		deleteCustomField: deleteCustomFieldFromContext,
		cancelEditCustomField,
		employeeGroupData,
		updateEmployeeGroupData,
		addEmployeeGroup: addEmployeeGroupToContext,
		editEmployeeGroup,
		deleteEmployeeGroup,
		cancelEditEmployeeGroup,
	} = useCustomizedData();

	const addEmployee = () => {
		if (!employeeData.employeeName) {
			showError("Please enter an employee name.");
			return;
		}
		if (!employeeData.employeeRole) {
			showError("Please select an employee role.");
			return;
		}

		const employeeToSave = {
			name: employeeData.employeeName,
			manager: employeeData.employeeManager ? employeeData.employeeManager.value : "",
			role: employeeData.employeeRole.value,
		};

		if (!employeeData.editingEmployeeId) {
			employeeToSave.id = Date.now();
		}

		addEmployeeToContext(employeeToSave);
		hideError();
	};

	const addCustomField = () => {
		if (!customFieldData.customFieldName) {
			showError("Please enter a field name for the employee data field.");
			return;
		}
		if (!customFieldData.customFieldType) {
			showError("Please select a field type for the employee data field.");
			return;
		}
		const customFieldToSave = {
			name: customFieldData.customFieldName,
			type: customFieldData.customFieldType.value,
		};

		// Handle choices for single-select and multi-select fields
		if (
			(customFieldData.customFieldType.value === "single-select" ||
				customFieldData.customFieldType.value === "multi-select") &&
			customFieldData.customFieldOptions
		) {
			if (typeof customFieldData.customFieldOptions === "string") {
				customFieldToSave.options = customFieldData.customFieldOptions
					.split(",")
					.map((option) => option.trim())
					.filter((option) => option.length > 0);
			} else {
				customFieldToSave.options = customFieldData.customFieldOptions;
			}
		}

		if (!customFieldData.editingCustomFieldId) {
			customFieldToSave.id = Date.now();
		}

		addCustomFieldToContext(customFieldToSave);
		hideError();
	};

	const addEmployeeGroup = () => {
		if (!employeeGroupData.employeeGroupName) {
			showError("Please enter a name for the employee group.");
			return;
		}
		addEmployeeGroupToContext();
		hideError();
	};

	return (
		<div>
			{/* Employee Section */}
			<div>
				<Text variant="h2" weight="bold">
					{employeeData.editingEmployeeId ? "Edit Employee" : "Add Employee"}
				</Text>

				<Spacer size={1} />
				<Text variant="caption">The manager must be created before the direct report.</Text>
				<Spacer size={1} />
				<Text variant="body" weight="bold">
					Employee Name
				</Text>
				<TextInput
					value={employeeData.employeeName}
					onChange={(val) => {
						updateEmployeeData({ employeeName: val });
						hideError();
					}}
				/>
				<Spacer size={1} />
				<Text variant="body" weight="bold">
					Employee's Manager
				</Text>
				<Spacer size={1} />
				<Select
					options={employeeData.employees.map((employee) => ({
						label: employee.name,
						value: employee.name,
					}))}
					value={employeeData.employeeManager}
					onChange={(val) => updateEmployeeData({ employeeManager: val })}
				/>
				<Spacer size={1} />
				<Text variant="body" weight="bold">
					Employee's Role
				</Text>
				<Select
					options={[
						{ label: "Billing Admin", value: "billing_admin" },
						{ label: "Admin", value: "admin" },
						{ label: "Basic Admin", value: "basic_admin" },
						{ label: "Employee", value: "employee" },
					]}
					value={employeeData.employeeRole}
					onChange={(val) => {
						updateEmployeeData({ employeeRole: val });
						hideError();
					}}
				/>

				<Spacer size={1} />
				<div style={{ display: "flex", gap: "10px" }}>
					<Button size="small" onClick={() => addEmployee()}>
						{employeeData.editingEmployeeId ? "Update Employee" : "Add Employee"}
					</Button>
					{employeeData.editingEmployeeId && (
						<Button
							size="small"
							color="gray"
							onClick={() => {
								cancelEditEmployee();
								hideError();
							}}
						>
							Cancel
						</Button>
					)}
				</div>
				{error && (error.includes("employee name") || error.includes("employee role")) && (
					<>
						<Spacer size={2} />
						<div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
							<Text variant="h3" weight="bold" style={{ color: "red" }}>
								{error}
							</Text>
						</div>
						<Spacer size={1} />
					</>
				)}
				<Spacer size={2} />
				{employeeData.employees.length > 0 && (
					<EmployeesTable
						employees={employeeData.employees}
						onEdit={editEmployeeInContext}
						onDelete={deleteEmployeeFromContext}
					/>
				)}
			</div>
			<Spacer size={4} />
			{/* Custom Field Section */}
			<div>
				<Text variant="h2" weight="bold">
					{customFieldData.editingCustomFieldId ? "Edit Employee Data Field" : "Add Employee Data Field"}
				</Text>
				<Spacer size={1} />
				<Text variant="body" weight="bold">
					Field Name
				</Text>
				<TextInput
					value={customFieldData.customFieldName}
					onChange={(val) => {
						updateCustomFieldData({ customFieldName: val });
						hideError();
					}}
				/>
				<Spacer size={1} />
				<Text variant="body" weight="bold">
					Field Type
				</Text>
				<Select
					options={CUSTOM_FIELD_TYPES}
					value={customFieldData.customFieldType}
					onChange={(val) => {
						updateCustomFieldData({ customFieldType: val });
						hideError();
					}}
				/>
				<Spacer size={1} />
				{(customFieldData.customFieldType?.value === "multi-select" ||
					customFieldData.customFieldType?.value === "single-select") && (
					<>
						<Text variant="body" weight="bold">
							Data Field Options
						</Text>
						<Spacer size={1} />
						<Text variant="caption">
							Enter the options for the multiple-select or single-select data field, separated by commas.
						</Text>
						<TextInput
							value={customFieldData.customFieldOptions}
							onChange={(val) => updateCustomFieldData({ customFieldOptions: val })}
						/>
						<Spacer size={1} />
					</>
				)}
			</div>
			<Spacer size={2} />
			<div style={{ display: "flex", gap: "10px" }}>
				<Button size="small" onClick={() => addCustomField()}>
					{customFieldData.editingCustomFieldId ? "Update Custom Field" : "Add Custom Field"}
				</Button>
				{customFieldData.editingCustomFieldId && (
					<Button
						size="small"
						color="gray"
						onClick={() => {
							cancelEditCustomField();
							hideError();
						}}
					>
						Cancel
					</Button>
				)}
			</div>
			{error && error.includes("employee data field") && (
				<>
					<Spacer size={2} />
					<div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
						<Text variant="h3" weight="bold" style={{ color: "red" }}>
							{error}
						</Text>
					</div>
					<Spacer size={1} />
				</>
			)}
			<Spacer size={2} />
			{customFieldData.customFields.length > 0 && (
				<CustomFieldsTable
					customFields={customFieldData.customFields}
					onEdit={editCustomFieldInContext}
					onDelete={deleteCustomFieldFromContext}
				/>
			)}
			<Spacer size={4} />
			{/* Employee Group Section */}
			<div>
				<Text variant="h2" weight="bold">
					{employeeGroupData.editingEmployeeGroupId ? "Edit Employee Group" : "Add Employee Group"}
				</Text>
				<Spacer size={1} />
				<Text variant="body" weight="bold">
					Employee Group Name
				</Text>
				<TextInput
					value={employeeGroupData.employeeGroupName}
					onChange={(val) => {
						updateEmployeeGroupData({ employeeGroupName: val });
						hideError();
					}}
				/>
				<Spacer size={1} />
				<Text variant="body" weight="bold">
					Employee Group Employees
				</Text>
				<Spacer size={1} />
				{employeeData.employees.length === 0 ? (
					<>
						<Spacer size={1} />
						<Text variant="caption" weight="bold" style={{ color: "#666", fontStyle: "italic" }}>
							No employees found. Please add employees first.
						</Text>
						<Spacer size={1} />
					</>
				) : (
					<Multiselect
						options={employeeData.employees.map((employee) => ({
							label: employee.name,
							id: employee.id,
						}))}
						value={employeeGroupData.employeeGroupEmployees}
						onChange={(val) => updateEmployeeGroupData({ employeeGroupEmployees: val })}
						columns={4}
					/>
				)}
				<Spacer size={1} />
				<Text variant="body" weight="bold">
					Employee Group Admins
				</Text>
				<Spacer size={1} />
				{employeeData.employees.length === 0 ? (
					<>
						<Spacer size={1} />
						<Text variant="caption" weight="bold" style={{ color: "#666", fontStyle: "italic" }}>
							No employees found. Please add employees first.
						</Text>
						<Spacer size={1} />
					</>
				) : (
					<Multiselect
						options={employeeData.employees.map((employee) => ({
							label: employee.name,
							id: employee.id,
						}))}
						value={employeeGroupData.employeeGroupAdmins}
						onChange={(val) => updateEmployeeGroupData({ employeeGroupAdmins: val })}
						columns={4}
					/>
				)}
				<Spacer size={1} />
				<Text variant="body" weight="bold">
					Allow Admins to create goals
				</Text>
				<Select
					options={BOOLEAN_OPTIONS}
					value={employeeGroupData.employeeGroupAdminPermissions}
					onChange={(val) => updateEmployeeGroupData({ employeeGroupAdminPermissions: val })}
				/>
				<Spacer size={2} />
				<div style={{ display: "flex", gap: "10px" }}>
					<Button size="small" onClick={() => addEmployeeGroup()}>
						{employeeGroupData.editingEmployeeGroupId ? "Update Employee Group" : "Add Employee Group"}
					</Button>
					{employeeGroupData.editingEmployeeGroupId && (
						<Button
							size="small"
							color="gray"
							onClick={() => {
								cancelEditEmployeeGroup();
								hideError();
							}}
						>
							Cancel
						</Button>
					)}
				</div>
			</div>
			{error && error.includes("employee group") && (
				<>
					<Spacer size={2} />
					<div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
						<Text variant="h3" weight="bold" style={{ color: "red" }}>
							{error}
						</Text>
					</div>
					<Spacer size={1} />
				</>
			)}
			<Spacer size={2} />
			{employeeGroupData.employeeGroups.length > 0 && (
				<EmployeeGroupTable
					employeeGroups={employeeGroupData.employeeGroups}
					employees={employeeData.employees}
					onEdit={editEmployeeGroup}
					onDelete={deleteEmployeeGroup}
				/>
			)}
		</div>
	);
};

export default EmployeeData;
